import React, { useState } from "react";
import api, { formatErr } from "../../../lib/api";
import { card, label, btn, btnGhost, downloadBlob } from "../drawings/_drawingsCommon";

export default function MTOPanel({ projectId }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("piping");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const extract = async () => {
    setBusy(true); setErr(""); setData(null);
    try {
      const { data } = await api.post(`/projects/${projectId}/mto/extract`, {});
      setData(data);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || String(e));
    } finally { setBusy(false); }
  };

  const exportFile = async (fmt) => {
    setErr("");
    try {
      const { data } = await api.post(`/projects/${projectId}/mto.${fmt}`, {}, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      downloadBlob(url, `MTO-${projectId.slice(0, 8)}.${fmt}`);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      try {
        const txt = await e?.response?.data?.text?.();
        setErr(formatErr(txt && JSON.parse(txt).detail) || String(e));
      } catch { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
    }
  };

  return (
    <div data-testid="mto-panel" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ ...card, flexDirection: "row", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ ...label, marginBottom: 2 }}>Material Take-Off Extractor</div>
          <div style={{ fontSize: 11, color: "var(--pf-muted)" }}>
            Walks the PDO + 3-D model and produces piping (per line + by class), valve, structural steel and instrument MTO.
          </div>
        </div>
        <button data-testid="mto-extract" style={btn} onClick={extract} disabled={busy}>
          {busy ? "Extracting…" : "Extract MTO"}
        </button>
        {data && (
          <>
            <button data-testid="mto-xlsx" style={btnGhost} onClick={() => exportFile("xlsx")}>Download XLSX</button>
            <button data-testid="mto-csv" style={btnGhost} onClick={() => exportFile("csv")}>Download CSV</button>
          </>
        )}
      </div>

      {err && <div style={{ padding: 8, color: "#FF8888", background: "rgba(239,68,68,.1)", borderRadius: 4, fontSize: 12 }}>{err}</div>}

      {data && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 8 }}>
            <Kpi label="Lines" value={data.piping.total_lines} testid="mto-kpi-lines" />
            <Kpi label="Total length (m)" value={data.piping.total_length_m} testid="mto-kpi-length" />
            <Kpi label="Valves" value={data.valves.total_valves} testid="mto-kpi-valves" />
            <Kpi label="Instruments" value={data.instruments.total_instruments} testid="mto-kpi-instruments" />
            <Kpi label="Steel (t)" value={data.steel.total_weight_t} testid="mto-kpi-steel" />
          </div>

          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {[["piping", "Piping"], ["byclass", "Piping by Class"], ["valves", "Valves"],
              ["steel", "Steel"], ["instruments", "Instruments"]].map(([k, lbl]) => (
              <button key={k} data-testid={`mto-tab-${k}`}
                style={tab === k ? btn : btnGhost} onClick={() => setTab(k)}>{lbl}</button>
            ))}
          </div>

          <div style={card}>
            {tab === "piping" && <Table headers={["Line", "Size (in)", "Class", "Material", "Length (m)", "Elbows-90", "Tees", "Reducers", "Flanges", "Bolts"]}
              rows={data.piping.lines.map((r) => [r.line_number, r.size_in, r.class, r.material, r.length_m, r.elbows_90, r.tees, r.reducers, r.flanges, r.bolts_sets])} />}
            {tab === "byclass" && <Table headers={["Class", "Lines", "Length (m)", "Elbows-90", "Flanges", "Bolts"]}
              rows={data.piping.by_class.map((r) => [r.class, r.lines, r.length_m, r.elbows_90, r.flanges, r.bolts_sets])} />}
            {tab === "valves" && <Table headers={["Tag", "Type", "Size (in)", "Rating", "Material", "Line", "Qty"]}
              rows={data.valves.valves.map((r) => [r.tag, r.type, r.size_in, r.rating, r.material, r.line, r.quantity])} />}
            {tab === "steel" && <Table headers={["Profile", "Material", "Length (m)", "Weight (kg)", "Count"]}
              rows={data.steel.profiles.map((r) => [r.profile, r.material, r.length_m, r.weight_kg, r.count])} />}
            {tab === "instruments" && <Table headers={["Tag", "Type", "Service"]}
              rows={data.instruments.instruments.map((r) => [r.tag, r.type, r.service])} />}
          </div>
        </>
      )}

      {!data && !busy && (
        <div style={{ ...card, textAlign: "center", color: "var(--pf-muted)", padding: 24 }}>
          Click "Extract MTO" to walk the latest run's PDO and produce a full Material Take-Off.
        </div>
      )}
    </div>
  );
}

function Kpi({ label: l, value, testid }) {
  return (
    <div data-testid={testid} style={{ padding: 10, background: "var(--pf-surface)", border: "1px solid var(--pf-border)", borderRadius: 6 }}>
      <div style={{ fontSize: 9, color: "var(--pf-muted)", textTransform: "uppercase", letterSpacing: 0.4 }}>{l}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: "#F26B1F", marginTop: 2 }}>{value}</div>
    </div>
  );
}

function Table({ headers, rows }) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
        <thead>
          <tr style={{ background: "var(--pf-surface-2)" }}>
            {headers.map((h) => <th key={h} style={{ padding: 6, textAlign: "left", color: "var(--pf-muted)", textTransform: "uppercase", fontSize: 9 }}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={{ borderBottom: "1px solid var(--pf-border)" }}>
              {r.map((c, j) => <td key={j} style={{ padding: 6, fontFamily: j === 0 ? "monospace" : "inherit" }}>{c}</td>)}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={headers.length} style={{ padding: 16, textAlign: "center", color: "var(--pf-muted)" }}>No data.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
